import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ListItemThumbnail from 'LuxKit/List/ListItemThumbnail'
import ListItemLoadingSkeleton from 'LuxKit/List/ListItemLoadingSkeleton'
import useMerchandiseOffer from 'hooks/Merchandise/useMerchandiseOffer'
import useMerchandiseBrand from 'hooks/Merchandise/useMerchandiseBrand'
import useMerchandiseCategory from 'hooks/Merchandise/useMerchandiseCategory'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { rem } from 'polished'
import Image from 'components/Common/Image'
import styled from 'styled-components'
import { getMerchandiseCategorySearchUrl, getMerchandiseBrandSearchUrl } from '../merchandiseUtils'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import LineShoppingBagIcon from 'LuxKit/Icons/line/LineShoppingBagIcon'
import { useOfferInViewTracking } from 'hooks/useOfferInViewTracking'

const ShoppingIconWrapper = styled.div`
  width: ${rem(48)};
  height: ${rem(48)};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
`

interface Props {
  itemId: string
  type: 'category' | 'brand' | 'product'
  index: number
  setClosed?: () => void
}

function MerchandiseSearchFormResultItem(props: Props) {
  const { itemId, type, index, setClosed } = props
  const [isInvalidImage, setIsInvalidImage] = useState(false)
  const [merchandise] = useMerchandiseOffer(itemId, { disabled: type !== 'product' })
  const [brand] = useMerchandiseBrand(itemId, { disabled: type !== 'brand' })
  const [category] = useMerchandiseCategory({ id: itemId }, { disabled: type !== 'category' })

  const { setTileRef, trackingInView } = useOfferInViewTracking()
  const dispatchOfferListEvent = useContext(OfferListEventsContext)

  useEffect(() => {
    if (trackingInView && merchandise) {
      dispatchOfferListEvent({
        type: OfferListEvents.productImpression,
        offer: merchandise,
        position: index,
        key: EventDataKey.ProductImpression,
      })
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingInView])

  const handleClick = useCallback(() => {
    if (merchandise) {
      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer: merchandise,
        position: index,
        key: EventDataKey.ProductClick,
      })
      setClosed?.()
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [merchandise, index])

  const itemData = useMemo(() => {
    switch (type) {
      case 'product':
        return {
          label: merchandise?.name,
          subtitle: merchandise?.brand.name,
          image: merchandise?.variants[0]?.images[0] ?? merchandise?.images[0],
          price: merchandise?.variants[0].price,
          to: `/shop/offer/${merchandise?.slug}/${merchandise?.id}`,
        }
      case 'brand':
        return {
          label: brand?.name,
          image: brand?.logo ?? brand?.image,
          to: brand ? getMerchandiseBrandSearchUrl(brand) : undefined,
        }
      case 'category':
        return {
          label: category?.name,
          image: category?.image,
          to: category ? getMerchandiseCategorySearchUrl(category) : undefined,
        }
    }
  }, [type, merchandise, brand, category])

  const listImage = useMemo(() => {
    if (itemData.image && !isInvalidImage) {
      return <ListItemThumbnail>
        <div ref={setTileRef}>
          <Image
            fit="center"
            image={itemData.image}
            height={48}
            width={48}
            onError={() => setIsInvalidImage(true)}
          />
        </div>
      </ListItemThumbnail>
    }

    return <div ref={setTileRef}>
      <ShoppingIconWrapper>
        <LineShoppingBagIcon colour="light-foreground-alt" />
      </ShoppingIconWrapper>
    </div>
  }, [itemData.image, isInvalidImage, setTileRef])

  if (!itemData.label) {
    return <ListItemLoadingSkeleton
      startIcon
      subtitle={type === 'product'}
    />
  }

  return <ClickableListItem
    title={<b>{itemData.label}</b>}
    subtitle={itemData.subtitle}
    startIcon={listImage}
    to={itemData.to}
    onClick={handleClick}
  />
}

export default MerchandiseSearchFormResultItem
